import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useInplaceEdit } from '../../contexts/component-details-page-inplace-edit/ComponentDetailsPageInplaceEditContext';
import { useGetComponentDetails } from '../../hooks/api/components';
import { useAuth } from '../../hooks/auth/useAuth';
import { AppRoutes } from '../../shared/enums/app-routes';
import BlurredComponentDetailsPage from './BlurredComponentDetailsPage';
import ComponentBreadCrumb from './components/component-breadcrumb/ComponentBreadCrumb';
import ComponentNameSection from './components/component-name-section/ComponentNameSection';
import ComponentTabView from './components/component-tab-view/ComponentTabView';

const ComponentDetailsPage = (): JSX.Element => {
  // Navigate to products page for BASF User
  const { isBasfUser } = useAuth();

  if (isBasfUser) {
    return <Navigate to={AppRoutes.Products} replace />;
  }
  // Get productId from URL
  const { componentId } = useParams() as { componentId: string };

  // Destructure the inplace edit context
  const { setComponentId: setContextComponentId } = useInplaceEdit();

  const queryClient = useQueryClient();

  // Fetch JSON data from Pacific API
  const {
    isLoading,
    isError,
    data: componentDetails,
  } = useGetComponentDetails(componentId, {
    retry: 1,
    onSuccess: () => {
      // Invalidate components list since visiting component details page will update the status to completed
      queryClient.invalidateQueries({
        predicate: (query) => {
          return query.queryKey[0] === 'components' && typeof query.queryKey[1] === 'object';
        },
      });
    },
  });

  // Set the componentId in the inplace edit context
  useEffect(() => {
    setContextComponentId(componentId);
  }, [componentId, setContextComponentId]);

  const isRestricted = useMemo(() => {
    return componentDetails?.restricted ?? false;
  }, [componentDetails]);

  if (
    isError ||
    (!isLoading && !(componentDetails || Object.keys(componentDetails).length === 0))
  ) {
    return <Navigate to='/404' replace={true} />;
  }

  return (
    <div className='px-6 py-5 flex flex-column gap-2'>
      {/* Breadcrumb */}
      <ComponentBreadCrumb isLoading={isLoading} componentName={componentDetails?.productName} />

      {/* Component Name Section */}
      <ComponentNameSection
        isLoading={isLoading}
        mainProductName={componentDetails?.productName}
        ownProductName={componentDetails?.ownProductName}
      />

      <div className='flex flex-column w-full'>
        {isRestricted ? <BlurredComponentDetailsPage /> : <ComponentTabView />}
      </div>
    </div>
  );
};

export default ComponentDetailsPage;
